import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Layout/Navbar";
import { Footer } from "@/components/Layout/Footer";
import { ArrowRight } from "lucide-react";
import { CreatorPromptCard } from "@/components/Feed/CreatorPromptCard";
import { ProductShowcase } from "@/components/Home/ProductShowcase";
import { CreatorShowcase } from "@/components/Home/CreatorShowcase";
import { HERO_SEED_PROMPT } from "@/data/homepage-seed";
import { GeoQualityBadge } from "@/components/GEO/GeoQualityBadge";
import { GeoQualityProgress } from "@/components/GEO/GeoQualityProgress";
import { calculateGeoQualityScore } from "@/lib/geo-quality-score";

export default function Index() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Scroll reveal — one-shot, simple
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll(".reveal-on-scroll").forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="min-h-screen text-white relative bg-tps-dark">
      {/* ───────────────── NAVBAR (shared component) ───────────────── */}
      <Navbar />

      {/* ───────────────── HERO ───────────────── */}
      <section className="pt-28 sm:pt-36 pb-20 sm:pb-28 px-5 sm:px-8">
        <div className="max-w-[1100px] mx-auto">
          <div className="grid lg:grid-cols-[1fr,450px] gap-12 lg:gap-8 items-center">
            {/* Left Column: Copy */}
            <div className="max-w-[720px]">
              {/* Headline */}
              <h1
                className="font-display font-bold leading-[1.1] mb-6 text-tps-primary tracking-[-0.02em]"
                style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)" }}
              >
                Where AI-native work lives.
              </h1>

              {/* Sub-headline */}
              <p className="font-sans text-base sm:text-lg leading-[1.7] mb-8 max-w-[600px] text-tps-secondary">
                ThePromptSpace is creator infrastructure. Publish prompts, workflows, and AI
                assets that are ranked by performance — not popularity. Build a body of work
                that compounds.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-start gap-4 mb-8">
                <Button
                  onClick={() => navigate("/prompts")}
                  className="font-display font-medium text-[15px] px-6 py-3 rounded-lg transition-colors bg-brand-cyan text-brand-dark h-[46px] hover:bg-brand-sky"
                >
                  Explore the platform
                </Button>
                <Link
                  to="/docs"
                  className="flex items-center gap-1.5 font-sans text-sm font-medium transition-colors group py-3 text-tps-secondary hover:text-white"
                >
                  Read the docs
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>

              {/* Status indicator */}
              <p className="font-mono text-xs tracking-wide text-tps-ghost">
                GEO v1 · live · ranking creator assets
              </p>
            </div>

            {/* Right Column: Hero Prompt Card */}
            <div className="hidden lg:block relative reveal-on-scroll">
              <div className="absolute -top-4 -right-4 z-20 pointer-events-none">
                <div className="glass-surface rounded-full px-3 py-1 shadow-2xl flex items-center gap-2">
                  <GeoQualityBadge qualityScore={calculateGeoQualityScore(HERO_SEED_PROMPT)} compact />
                </div>
              </div>
              <div className="relative z-10 pointer-events-auto">
                <CreatorPromptCard prompt={HERO_SEED_PROMPT} />
              </div>
              
              {/* Decorative background glow for depth */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 bg-brand-cyan/20 blur-[100px] rounded-full pointer-events-none -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── PRODUCT SHOWCASE ───────────────── */}
      <section className="py-12 sm:py-20 px-5 sm:px-8">
        <div className="max-w-[1100px] mx-auto">
          <ProductShowcase />
        </div>
      </section>

      {/* ───────────────── WHAT THEPROMPTSPACE IS (Merged with Problem) ───────────────── */}
      <section className="py-24 sm:py-32 px-5 sm:px-8">
        <div className="max-w-[800px] mx-auto">
          <div className="reveal-on-scroll mb-14">
            <h2 className="font-display font-semibold text-2xl sm:text-[2.5rem] leading-[1.15] mb-6 text-tps-primary tracking-[-0.01em]">
              Creator infrastructure, built for AI-native&nbsp;work.
            </h2>
            <p className="font-sans text-base sm:text-lg leading-[1.7] text-tps-secondary">
              Millions of people create with AI every day. Prompts, workflows, chains, systems — real intellectual work. But it lives in chat logs, dies in browser tabs, and gets copied without credit. There is no system to publish it, rank it, own it, or build a career from it. Until now.
            </p>
          </div>

          <div className="space-y-0">
            {[
              {
                label: "Publish",
                desc: "Prompts, workflows, and AI assets with structured metadata, versioning, and attribution. Not a post in a feed. A permanent, searchable, credited artifact.",
              },
              {
                label: "Rank",
                desc: "GEO (Generative Engine Optimization) scores every creation based on reuse, originality, and real-world performance. What works rises. What doesn\u2019t fades quietly.",
              },
              {
                label: "Own",
                desc: "Your profile is a living portfolio. Your work is licensed by default. Your reputation is earned through output, not followers.",
              },
            ].map((pillar, i) => (
              <div
                key={pillar.label}
                className="reveal-on-scroll flex flex-col sm:flex-row gap-3 sm:gap-8 py-8"
                style={{
                  borderBottom: i < 2 ? "1px solid var(--tps-border)" : "none",
                  transitionDelay: `${i * 80}ms`,
                }}
              >
                <span className="font-display font-bold text-lg sm:text-xl shrink-0 sm:w-28 text-brand-cyan">
                  {pillar.label}
                </span>
                <p className="font-sans text-[15px] sm:text-base leading-[1.7] text-tps-secondary">
                  {pillar.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────── GEO EXPLAINER ───────────────── */}
      <section className="py-24 sm:py-32 px-5 sm:px-8">
        <div className="max-w-[1100px] mx-auto">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-start">
            {/* Left — copy */}
            <div className="reveal-on-scroll max-w-[540px]">
              <h2 className="font-display font-semibold text-2xl sm:text-[2.5rem] leading-[1.15] mb-6 text-tps-primary tracking-[-0.01em]">
                GEO: Ranking for the AI&nbsp;era.
              </h2>
              <div className="space-y-4">
                <p className="font-sans text-[15px] sm:text-base leading-[1.75] text-tps-secondary">
                  Search engines rank web pages. Social platforms rank posts. GEO ranks
                  AI-native creations — by how they perform, not how they trend.
                </p>
                <p className="font-sans text-[15px] sm:text-base leading-[1.75] text-tps-secondary">
                  Reuse frequency, originality depth, real-world outcomes. GEO is the
                  system that decides what surfaces and what fades. It's not a feed
                  algorithm. It's a quality signal.
                </p>
              </div>
            </div>

            {/* Right — ranking visualization */}
            <div
              className="reveal-on-scroll relative max-w-[400px] w-full"
              style={{ transitionDelay: "120ms" }}
            >
              <GeoQualityProgress 
                promptData={{
                  title: HERO_SEED_PROMPT.title,
                  description: HERO_SEED_PROMPT.description || "",
                  content: HERO_SEED_PROMPT.content,
                  category: HERO_SEED_PROMPT.category,
                }}
                geoData={{
                  intent: HERO_SEED_PROMPT.geoMetadata?.intent || "",
                  useCase: HERO_SEED_PROMPT.geoMetadata?.use_case || "",
                  recommendedModel: HERO_SEED_PROMPT.geoMetadata?.models?.recommended || "",
                  testedByAuthor: HERO_SEED_PROMPT.geoMetadata?.reliability?.tested_by_author || false,
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── CREATOR SHOWCASE ───────────────── */}
      <section className="py-24 sm:py-32 px-5 sm:px-8 bg-tps-dark relative overflow-hidden">
        <div className="max-w-[1100px] mx-auto relative z-10">
          <div className="text-center max-w-[640px] mx-auto mb-16 reveal-on-scroll">
            <h2 className="font-display font-semibold text-2xl sm:text-[2.5rem] leading-[1.15] mb-6 text-tps-primary tracking-[-0.01em]">
              Your work is your identity.
            </h2>
            <p className="font-sans text-base sm:text-lg leading-[1.7] text-tps-secondary">
              A profile on ThePromptSpace isn't a feed of posts. It's a permanent portfolio of the intellectual property you've created. Build your reputation through output, not followers.
            </p>
          </div>
          
          <CreatorShowcase />
        </div>
      </section>

      {/* ───────────────── FOUNDER'S NOTE ───────────────── */}
      <section className="py-16 sm:py-24 px-5 sm:px-8 border-t border-tps">
        <div className="max-w-[700px] mx-auto reveal-on-scroll">
          <div className="glass-surface p-8 sm:p-12 rounded-2xl">
            <blockquote>
              <p className="font-sans text-base sm:text-[17px] leading-[1.8] italic text-tps-secondary">
                I'm building ThePromptSpace because AI-native work deserves the same
                infrastructure that code, design, and music already have. A place to
                publish. A system to rank quality. A way to build a career. This is
                early. The product is real. The vision is long-term.
              </p>
              <footer className="mt-8 flex items-center gap-4">
                <img 
                  src="https://avatars.githubusercontent.com/u/124599?v=4" 
                  alt="Navakishor" 
                  className="w-12 h-12 rounded-full border border-tps grayscale opacity-80"
                />
                <div>
                  <p className="font-display font-semibold text-sm text-tps-primary">
                    Navakishor
                  </p>
                  <p className="font-sans text-xs mt-0.5 text-tps-tertiary">
                    Founder, ThePromptSpace
                  </p>
                </div>
              </footer>
            </blockquote>
          </div>
        </div>
      </section>

      {/* ───────────────── CONVERSION CTA ───────────────── */}
      <section className="py-24 sm:py-32 px-5 sm:px-8">
        <div className="max-w-[640px] mx-auto text-center reveal-on-scroll">
          <h2 className="font-display font-bold text-3xl sm:text-5xl leading-[1.1] mb-5 text-tps-primary tracking-[-0.02em]">
            Start building.
          </h2>
          <p className="font-sans text-base sm:text-[17px] leading-[1.7] mb-8 text-tps-secondary">
            Publish your first prompt. Build your creator profile. Get ranked by GEO.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => navigate(user ? "/create-prompt" : "/auth")}
              className="font-display font-medium text-[15px] px-7 py-3 rounded-lg transition-colors bg-brand-cyan text-brand-dark h-[46px] hover:bg-brand-sky"
            >
              Publish your first prompt
            </Button>
            <Link
              to="/docs"
              className="flex items-center gap-1.5 font-sans text-sm font-medium transition-colors group py-3 text-tps-secondary hover:text-white"
            >
              Read the documentation
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ───────────────── FOOTER (shared component) ───────────────── */}
      <Footer />
    </div>
  );
}